import React, { useState, useEffect } from "react";
import { Plus, Trash2, Eye, ShoppingCart, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import QuotationModal from "./QuotationModal";

const CheckoutForm = ({ onClose }) => {
  const existingCartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
  const initialFormData = {
    name: existingCartItems[0]?.name || "",
    mobile: existingCartItems[0]?.mobile || "",
    address: existingCartItems[0]?.address || "",
    pincode: existingCartItems[0]?.pincode || "",
    selectedLocation: existingCartItems[0]?.selectedLocation || "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [pincodeData, setPincodeData] = useState([]);
  const businessPhoneNumber = "+91 94279 86568";

  // SEO and accessibility improvements
  useEffect(() => {
    // Set page title for SEO
    document.title = "Checkout - Complete Your Order | NaturalX";

    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Complete your order with our secure checkout process. Enter your details and proceed with WhatsApp order confirmation.",
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Complete your order with our secure checkout process. Enter your details and proceed with WhatsApp order confirmation.";
      document.head.appendChild(meta);
    }

    // Focus management for accessibility
    const firstInput = document.querySelector('input[name="name"]');
    if (firstInput) {
      firstInput.focus();
    }

    // Cleanup function
    return () => {
      document.title = "NaturalX";
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleWhatsAppClick = () => {
    const cartItemsDetails = existingCartItems
      .map((item, index) => {
        return `(${index + 1}) Product: ${item.productName}, Pattern: ${
          item.productsPattern || "Standard"
        }, Rate: ₹${item.rate}, Area: ${item.sqft} sq ft, Apply Area: ${
          item.Area
        }, Discount: ${item.discount}%, Effective Rate: ₹${
          item.effectiveRate
        }, Subtotal: ₹${item.amount}, Total: ₹${parseFloat(
          item.amountWithIgst || 0,
        ).toFixed(2)} (incl. taxes)`;
      })
      .join("\n");

    const total = existingCartItems.reduce(
      (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
      0,
    );

    const whatsappMessage = `Hello, I would like to proceed with my order.\n\nOrder Details:\n${cartItemsDetails}\n\nCustomer Details:\nName: ${
      formData.name
    }\nMobile: ${formData.mobile}\nAddress: ${formData.address}\nPincode: ${
      formData.pincode
    }\nLocation: ${formData.selectedLocation}\n\nTotal Amount: ₹${total.toFixed(
      2,
    )}`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${businessPhoneNumber}&text=${encodeURIComponent(
      whatsappMessage,
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const fetchPincodeData = async (pincode) => {
    if (pincode.length === 6) {
      try {
        const response = await fetch(
          `https://api.postalpincode.in/pincode/${pincode}`,
        );
        const data = await response.json();
        if (data[0].Status === "Success") {
          setPincodeData(data[0].PostOffice);
        } else {
          setPincodeData([]);
        }
      } catch (error) {
        console.error("Error fetching pincode data:", error);
        setPincodeData([]);
      }
    } else {
      setPincodeData([]);
    }
  };

  useEffect(() => {
    if (formData.pincode.length === 6) {
      fetchPincodeData(formData.pincode);
    }
  }, [formData.pincode]);

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      valid = false;
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      valid = false;
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Mobile number must be 10 digits";
      valid = false;
    }

    if (!formData.address.trim()) {
      newErrors.address = "Address is required";
      valid = false;
    } else if (formData.address.trim().length < 10) {
      newErrors.address = "Please provide a complete address";
      valid = false;
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
      valid = false;
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Pincode must be 6 digits";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        const cartItemsDetails = existingCartItems
          .map((item, index) => {
            return `(${index + 1}) Product: ${item.productName}, Pattern: ${
              item.productsPattern || "Standard"
            }, Rate: ₹${item.rate}, Area: ${item.sqft} sq ft, Apply Area: ${
              item.Area
            }, Discount: ${item.discount}%, Effective Rate: ₹${
              item.effectiveRate
            }, Subtotal: ₹${item.amount}, Total: ₹${parseFloat(
              item.amountWithIgst || 0,
            ).toFixed(2)} (incl. taxes)`;
          })
          .join("\n");

        const total = existingCartItems.reduce(
          (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
          0,
        );

        const message = `New order from ${formData.name}. Address: ${
          formData.address
        }, Pincode: ${formData.pincode}, Location: ${
          formData.selectedLocation
        }\n\nOrder Details:\n${cartItemsDetails}\n\nTotal Amount: ₹${total.toFixed(
          2,
        )}`;

        const response = await fetch(
          `https://webadmin.rigve.com/api/save_contact?name=${encodeURIComponent(
            formData.name,
          )}&phone=${encodeURIComponent(
            formData.mobile,
          )}&email=${encodeURIComponent("-")}&msg=${encodeURIComponent(
            message,
          )}`,
        );

        if (!response.ok) {
          throw new Error("Failed to save contact");
        }

        console.log("Form submitted and contact saved:", formData);
        setSubmitSuccess(true);

        // Delay WhatsApp redirect to show success message
        setTimeout(() => {
          handleWhatsAppClick();
          onClose();
          localStorage.removeItem("cartItems");

          // Refresh the page
          window.location.reload();
        }, 1500);
      } catch (error) {
        console.error("Error saving contact:", error);
        setErrors({ submit: "Failed to submit order. Please try again." });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle escape key for accessibility
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-labelledby="checkout-title"
      aria-describedby="checkout-description"
    >
      <div className="relative w-full max-w-lg mx-auto bg-white backdrop-blur-xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto border border-gray-200">
        {/* Header */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-xl border-b border-gray-200 px-6 py-5 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <h2
              id="checkout-title"
              className="fs-21 font-semibold text-gray-900 tracking-tight"
            >
              Complete Your Cart
            </h2>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white hover:bg-gray-800 rounded-full p-2 transition-all duration-300"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <p className="mt-2 text-sm text-gray-500">
            Enter your details to proceed with WhatsApp order confirmation
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {submitSuccess ? (
            <div className="text-center py-10">
              <div className="mx-auto flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Order Submitted Successfully!
              </h3>

              <p className="text-sm text-gray-500">
                Redirecting to WhatsApp...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 text-sm rounded-xl border focus:ring-2 focus:ring-black transition ${
                    errors.name ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Enter your full name"
                  aria-describedby={errors.name ? "name-error" : undefined}
                />
                {errors.name && (
                  <p
                    id="name-error"
                    className="text-red-500 text-sm mt-1"
                    role="alert"
                  >
                    {errors.name}
                  </p>
                )}
              </div>
              {/* Mobile Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 text-sm rounded-xl border focus:ring-2 focus:ring-black ${
                    errors.mobile ? "border-red-500" : "border-gray-300"
                  }`}
                  maxLength="10"
                  pattern="[0-9]{10}"
                  placeholder="Enter 10-digit number"
                  aria-describedby={errors.mobile ? "mobile-error" : undefined}
                />
                {errors.mobile && (
                  <p
                    id="mobile-error"
                    className="text-red-500 text-sm mt-1"
                    role="alert"
                  >
                    {errors.mobile}
                  </p>
                )}
              </div>
              {/* Address Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="3"
                  className={`w-full px-4 py-3 text-sm rounded-xl border focus:ring-2 focus:ring-black resize-none ${
                    errors.address ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Complete address"
                  aria-describedby={
                    errors.address ? "address-error" : undefined
                  }
                />
                {errors.address && (
                  <p
                    id="address-error"
                    className="text-red-500 text-sm mt-1"
                    role="alert"
                  >
                    {errors.address}
                  </p>
                )}
              </div>
              {/* Pincode Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pincode *
                </label>
                <input
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 text-sm rounded-xl border focus:ring-2 focus:ring-black ${
                    errors.pincode ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="6-digit pincode"
                  id="pincode"
                  aria-describedby={
                    errors.pincode ? "pincode-error" : undefined
                  }
                />
                {errors.pincode && (
                  <p
                    id="pincode-error"
                    className="text-red-500 text-sm mt-1"
                    role="alert"
                  >
                    {errors.pincode}
                  </p>
                )}
              </div>
              {/* Location Dropdown */}
              {formData.pincode.length === 6 && pincodeData.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Location
                  </label>
                  <select
                    id="selectedLocation"
                    name="selectedLocation"
                    value={formData.selectedLocation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 text-sm rounded-xl border border-gray-300 focus:ring-2 focus:ring-black"
                    aria-describedby="location-help"
                  >
                    <option value="">Select location</option>
                    {pincodeData.map((location, index) => (
                      <option key={index} value={location.Name}>
                        {location.Name}
                      </option>
                    ))}
                  </select>
                  <p id="location-help" className="mt-2 text-xs text-gray-500">
                    Select the most accurate location for delivery
                  </p>
                </div>
              )}
              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600" role="alert">
                    {errors.submit}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-6 py-3 rounded-xl bg-red-300 hover:bg-red-600 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full sm:flex-1 px-6 py-3 rounded-xl text-white font-medium ${
                    isLoading
                      ? "bg-gray-400"
                      : "bg-gradient-to-r from-black to-gray-800 hover:opacity-90"
                  }`}
                >
                  {isLoading ? "Processing..." : "Submit Order"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 px-6 py-4 bg-gray-50 border-t text-xs text-gray-500 rounded-b-2xl">
          🔒 Your information is secure and used only for order processing
        </div>
      </div>
    </div>
  );
};

const CartPage = () => {
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showQuotationModal, setShowQuotationModal] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Shopping Cart - Your Interior Wall Textures Store";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Review your selected interior wall texture products. Secure checkout with competitive pricing and fast delivery.",
      );
    } else {
      const meta = document.createElement("meta");
      meta.name = "description";
      meta.content =
        "Review your selected interior wall texture products. Secure checkout with competitive pricing and fast delivery.";
      document.head.appendChild(meta);
    }

    try {
      const existingCartItems =
        JSON.parse(localStorage.getItem("cartItems")) || [];
      setCartItems(existingCartItems);

      const total = existingCartItems.reduce(
        (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
        0,
      );
      setTotalAmount(total);
    } catch (error) {
      console.error("Error loading cart items:", error);
      setCartItems([]);
      setTotalAmount(0);
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (showCheckout || showQuotationModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to reset the overflow when the component unmounts
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showCheckout, showQuotationModal]);

  const handleRemoveItem = async (index) => {
    if (isRemoving) return;
    setIsRemoving(true);

    try {
      const updatedCartItems = [...cartItems];
      updatedCartItems.splice(index, 1);
      localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));
      setCartItems(updatedCartItems);

      const total = updatedCartItems.reduce(
        (sum, item) => sum + parseFloat(item.amountWithIgst || 0),
        0,
      );
      setTotalAmount(total);

      await new Promise((resolve) => setTimeout(resolve, 500));
      window.location.reload();
    } catch (error) {
      console.error("Error removing item from cart:", error);
      alert("Failed to remove item from cart. Please try again.");
      setIsRemoving(false);
    }
  };

  const handleAddMoreProducts = () => {
    navigate("/Products/5/Interior%20Wall%20Textures");
  };

  const handleProceedToCheckout = () => {
    if (cartItems.length === 0) {
      alert("Your cart is empty. Please add products before checkout.");
      return;
    }
    setShowCheckout(true);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ShoppingCart",
    name: "Shopping Cart",
    description: "Interior wall texture products cart",
    offers: cartItems.map((item) => ({
      "@type": "Offer",
      name: item.productName,
      price: item.amountWithIgst,
      priceCurrency: "INR",
    })),
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <main className="bg-gradient-to-br from-white via-orange-100 to-white py-8">
        <div className="container mx-auto max-w-7xl px-4">
          <header className="mb-8">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 p-6 sm:p-8 text-white shadow-lg">
              {/* CONTENT */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="hidden sm:flex ">
                  <div className="bg-white/20 p-3 rounded-xl backdrop-blur-md">
                    <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                  </div>
                </div>
                <div className="w-full">
                  <div className="flex justify-between">
                    <h1 className="text-xl font-bold">Your Cart</h1>
                    <div className="bg-white/20 px-4 py-2 rounded-xl text-sm backdrop-blur-md">
                      {cartItems.length}{" "}
                      {cartItems.length === 1 ? "Item" : "Items"}
                    </div>
                  </div>

                  <p className="text-white/80 text-sm sm:text-base">
                    Review your selected wall textures
                  </p>
                </div>
              </div>
            </div>
          </header>
          {cartItems.length === 0 ? (
            <section className="text-center py-12 sm:py-12">
              <div className="max-w-md mx-auto">
                <ShoppingCart className="w-16 h-16 sm:w-20 sm:h-20 text-gray-400 mx-auto mb-6" />
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  Your cart is empty
                </h2>
                <p className="text-gray-600 mb-8">
                  Looks like you haven't added any interior wall textures yet.
                  Discover our premium collection and transform your space.
                </p>
                <button
                  onClick={handleAddMoreProducts}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Browse Products
                </button>
              </div>
            </section>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
              <section className="lg:col-span-2">
                {cartItems.map((item, index) => (
                  <div
                    key={index}
                    className="group relative bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 mb-5"
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* IMAGE SECTION */}
                      <div className="relative md:w-52 w-full h-40 md:h-auto overflow-hidden">
                        <img
                          src={item.mainImage}
                          alt={item.productName}
                          className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                        />

                        {/* PRICE BADGE */}
                      </div>

                      {/* CONTENT */}
                      <div className="flex-1 p-5 flex flex-col justify-between">
                        {/* HEADER */}
                        <div>
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 capitalize">
                                {item.productName}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {item.Area}
                              </p>
                            </div>

                            {/* REMOVE BUTTON */}
                            <button
                              onClick={() => handleRemoveItem(index)}
                              className="p-2 rounded-full bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition"
                            >
                              {isRemoving ? (
                                <div className="w-4 h-4 border-2 border-white border-t-red-600 rounded-full animate-spin"></div>
                              ) : (
                                <Trash2 className="w-5 h-5" />
                              )}
                            </button>
                          </div>

                          {/* DETAILS GRID */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3 text-sm">
                            <div className="bg-gray-100 rounded-xl p-2">
                              <p className="text-gray-500">Pattern</p>
                              <p className="font-medium text-gray-800">
                                {item.productsPattern || "Standard"}
                              </p>
                            </div>

                            <div className="bg-gray-100 rounded-xl p-2">
                              <p className="text-gray-500">Rate</p>
                              <p className="font-medium text-gray-800">
                                ₹{item.rate}
                              </p>
                            </div>

                            <div className="bg-gray-100 rounded-xl p-2">
                              <p className="text-gray-500">Area</p>
                              <p className="font-medium text-gray-800">
                                {item.sqft} sq ft
                              </p>
                            </div>

                            <div className="bg-gray-100 rounded-xl p-2">
                              <p className="text-gray-500">Discount</p>
                              <p className="font-medium text-gray-800">
                                {item.discount}%
                              </p>
                            </div>

                            <div className="bg-gray-100 rounded-xl p-2">
                              <p className="text-gray-500">Final Rate</p>
                              <p className="font-medium text-gray-800">
                                ₹{item.effectiveRate}
                              </p>
                            </div>

                            <div className="bg-gray-100 rounded-xl p-2">
                              <p className="text-gray-500">Subtotal</p>
                              <p className="font-medium text-gray-800">
                                ₹{item.amount}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* FOOTER */}
                        <div className="flex justify-between items-center mt-3">
                          {/* VIEW LINK */}
                          <a
                            href={item.pageUrl}
                            className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
                          >
                            <Eye className="w-4 h-4" />
                            View Details
                          </a>

                          {/* TOTAL */}
                          <p className="text-xl font-bold text-gray-900">
                            ₹{parseFloat(item.amountWithIgst || 0).toFixed(2)}
                            <span className="text-sm font-normal text-gray-500 ml-1">
                              (incl. taxes)
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </section>
              <aside className="lg:col-span-1">
                <div className="sticky top-6 bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  {/* HEADER */}
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Your Order
                    </h2>
                  </div>

                  {/* ITEMS */}
                  <div className="divide-y divide-gray-200 max-h-72 overflow-y-auto">
                    {cartItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 px-6 py-4"
                      >
                        {/* THUMBNAIL */}
                        <img
                          src={item.mainImage}
                          alt={item.productName}
                          className="w-14 h-14 object-cover rounded-lg border"
                        />

                        {/* DETAILS */}
                        <div className="flex-1">
                          <p className="text-base mb-2 font-medium text-gray-900 leading-tight">
                            {item.productName}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.productsPattern || "Standard"} • {item.sqft}{" "}
                            sq ft
                          </p>
                        </div>

                        {/* PRICE */}
                        <p className="text-sm font-semibold text-gray-800">
                          ₹{parseFloat(item.amountWithIgst || 0).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* TOTAL BLOCK */}
                  <div className="bg-slate-100 px-6 py-5 space-y-3">
                    <div className="flex justify-between text-sm text-gray-700">
                      <span>Subtotal</span>
                      <span>₹{totalAmount.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between text-sm text-gray-700">
                      <span>GST</span>
                      <span>Included</span>
                    </div>

                    <div className="border-t pt-3 flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900">
                        Total
                      </span>
                      <span className="text-2xl font-bold text-orange-600">
                        ₹{totalAmount.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="p-6 space-y-3">
                    <button
                      onClick={handleProceedToCheckout}
                      className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-xl transition"
                    >
                      Proceed to Checkout
                    </button>

                    <button
                      onClick={handleAddMoreProducts}
                      className="w-full py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition"
                    >
                      Add More Products
                    </button>

                    <button
                      onClick={() => setShowQuotationModal(true)}
                      className="w-full text-sm text-orange-600 hover:underline"
                    >
                      Download Quotation
                    </button>
                  </div>
                </div>
              </aside>
            </div>
          )}
        </div>
        {showCheckout && (
          <CheckoutForm onClose={() => setShowCheckout(false)} />
        )}
        {showQuotationModal && (
          <QuotationModal onClose={() => setShowQuotationModal(false)} />
        )}
      </main>
    </>
  );
};

export default CartPage;
